<template>
  <div class="binance-new-ui-container">
    <div class="new-ui-card">
      <!-- 1. Header Row: Search & Actions -->
      <div class="new-ui-header-row">
        <!-- Search Input -->
        <div class="new-ui-search-wrapper">
          <div class="new-ui-search-box">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              class="new-ui-search-input"
              v-model="searchQuery"
              @input="debouncedSearch"
              placeholder="Search by symbol..."
            />
          </div>
        </div>

        <!-- Filter Action toggler & Refresh buttons -->
        <div class="new-ui-header-actions">
          <!-- Multi Coin Dropdown Gộp -->
          <div class="new-ui-multicoin-dropdown relative">
            <button
              @click="isMultiCoinOpen = !isMultiCoinOpen"
              class="new-ui-filter-toggle-btn new-ui-btn-highlight"
              :class="{ 'active': isMultiCoinOpen }"
              title="Xem danh sách và vẽ biểu đồ Multi Coins"
            >
              <i class="fa-solid fa-layer-group"></i>
              <span>Call Multi ({{ multiCoinCount }})</span>
            </button>
            
            <div v-show="isMultiCoinOpen" class="new-ui-multicoin-menu-container">
              <div class="new-ui-multicoin-header">
                <span>Multi Coin List ({{ multiCoinCount }})</span>
              </div>
              
              <!-- Nút hành động vẽ chart đặt ngay trong dropdown panel -->
              <div class="new-ui-multicoin-action-box" v-if="multiCoinCount > 0">
                <button
                  @click="handleCallMultiInDropdown"
                  class="new-ui-dropdown-action-btn"
                >
                  <i class="fa-solid fa-chart-line"></i>
                  Vẽ biểu đồ nhóm này
                </button>
              </div>

              <div class="new-ui-multicoin-items-scroll">
                <div v-for="c in multiCoinList" :key="c.coinName" class="new-ui-multicoin-item">
                  <span class="new-ui-multicoin-item-name">{{ c.coinName.replace('USDT', '') }}</span>
                  <i class="fa-solid fa-xmark text-gray-400 hover:text-red-500 cursor-pointer" @click.stop="toggleIsCall(c)"></i>
                </div>
                <div v-if="multiCoinList.length === 0" class="new-ui-multicoin-empty">
                  No coins in call list
                </div>
              </div>
            </div>
          </div>



          <!-- Refresh button (icon only) -->
          <button
            @click="handleRefreshClick"
            class="new-ui-filter-toggle-btn"
            :disabled="isSearching"
            :title="$route.params.coinName ? 'Refresh ' + $route.params.coinName : 'Refresh Charts'"
          >
            <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isSearching }"></i>
          </button>
          <button
            @click="showTable = !showTable"
            class="new-ui-filter-toggle-btn new-ui-btn-highlight"
            title="Ẩn/Hiện danh sách coin"
          >
            <i class="fa-solid" :class="showTable ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            <span>{{ showTable ? 'Ẩn Symbols' : 'Hiện Symbols' }}</span>
          </button>
        </div>
      </div>

      <!-- 2. Advanced Filters Drawer (Collapsed by default) -->
      <div class="new-ui-advanced-drawer">
        <div class="drawer-grid">
          <div class="drawer-field">
            <label class="drawer-label">Timeframe</label>
            <select v-model="periods" class="drawer-input">
              <option value="1m">1m</option>
              <option value="3m">3m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h</option>
              <option value="4h">4h</option>
              <option value="6h">6h</option>
              <option value="12h">12h</option>
              <option value="1d">1d</option>
            </select>
          </div>

          <div class="drawer-field relative" @keydown.stop>
            <label class="drawer-label">Metrics</label>
            <button type="button"
              class="new-ui-metrics-select-btn"
              @click="isMetricsOpen = !isMetricsOpen" :aria-expanded="isMetricsOpen">
              <span class="truncate pr-4">{{ metricsLabel }}</span>
              <i class="fa-solid fa-chevron-down text-xs ml-auto"></i>
            </button>
            <div v-show="isMetricsOpen" ref="metricsMenu"
              class="new-ui-metrics-dropdown-menu">
              <div class="new-ui-metrics-items-scroll">
                <label v-for="opt in metricOptions" :key="opt.value"
                  class="new-ui-metrics-item-label">
                  <input type="checkbox" class="new-ui-metrics-checkbox" :value="opt.value" v-model="metricsProxy" />
                  <span>{{ opt.label }}</span>
                </label>
              </div>

              <div class="new-ui-metrics-footer">
                <button class="new-ui-metrics-footer-btn" @click="selectAll">Select all</button>
                <button class="new-ui-metrics-footer-btn" @click="clearAll">Unselect all</button>
              </div>
            </div>
          </div>

          <div class="drawer-field">
            <label class="drawer-label">Min Open Interest (M)</label>
            <input type="number" v-model.number="minOi" placeholder="e.g. 5" class="drawer-input" />
          </div>
          <div class="drawer-field">
            <label class="drawer-label">Max Open Interest (M)</label>
            <input type="number" v-model.number="maxOi" placeholder="e.g. 50" class="drawer-input" />
          </div>
          
          <!-- Search button moved here next to Max Open Interest (M) -->
          <div class="drawer-field" style="width: fit-content;">
            <label class="drawer-label">&nbsp;</label>
            <button @click="applySearch" class="drawer-btn-primary" :disabled="isSearching">
              <span v-if="!isSearching">Search</span>
              <span v-else class="loading-spinner"></span>
            </button>
          </div>

          <!-- Action buttons - canh lề phải -->
          <div class="drawer-field cg-action-group" style="margin-left: auto;">
            <label class="drawer-label">&nbsp;</label>
            <div class="cg-action-btns">
              <button @click="openExclusionManager" class="drawer-btn-secondary" style="white-space: nowrap;">
                <i class="fa-solid fa-list-check"></i> Exclusion Manager
              </button>
              <button
                @click="showCustomGroupModal = true"
                class="cg-main-trigger-btn"
                title="Custom Groups — Override tab hiển thị coin"
              >
                <i class="fa-solid fa-sliders"></i>
                Custom Groups
              </button>
              <button
                @click="showTabManager = true"
                class="cg-main-trigger-btn tab-manager-btn"
                title="Tab Manager — Tạo và quản lý custom tabs"
              >
                <i class="fa-solid fa-layer-group"></i>
                Tab Manager
                <span v-if="customTabs.length > 0" class="tm-tab-count-badge">{{ customTabs.length }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. Market Categories Tabs -->
      <div class="new-ui-market-tabs">
        <button
          class="new-ui-tab-btn tab-compact"
          :class="{ active: activeMarketTab === 'favorites' }"
          @click="handleTabClick('favorites')"
        >
          <i class="fa-solid fa-star"></i> <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.favorites }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'all' }"
          @click="handleTabClick('all')"
        >
          All Markets <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.all }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'filtered' }"
          @click="handleTabClick('filtered')"
        >
          <i class="fa-solid fa-filter"></i> Filter Exclusion <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.filtered }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'spot' }"
          @click="handleTabClick('spot')"
        >
          Spot Only <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.spot }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'spotfutures' }"
          @click="handleTabClick('spotfutures')"
        >
          Spot &amp; Futures <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.spotfutures }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'futures' }"
          @click="handleTabClick('futures')"
        >
          Futures Only <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.futures }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'alpha' }"
          @click="handleTabClick('alpha')"
        >
          Futures Alpha <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.alpha }}</span>
        </button>
        <button
          class="new-ui-tab-btn"
          :class="{ active: activeMarketTab === 'monitoring' }"
          @click="handleTabClick('monitoring')"
        >
          <i class="fa-solid fa-triangle-exclamation"></i>
          Monitoring <span class="tab-count-badge" v-if="allDbCoins.length || coins.length">{{ tabCounts.monitoring }}</span>
        </button>

        <!-- Dynamic Custom Tabs (trước OI Range) -->
        <button
          v-for="ctab in customTabs"
          :key="ctab.slug"
          class="new-ui-tab-btn tab-custom"
          :class="{ active: activeMarketTab === ctab.slug }"
          @click="handleTabClick(ctab.slug)"
        >
          <i class="fa-solid fa-layer-group"></i>
          {{ ctab.name }}
          <span class="tab-count-badge tab-count-badge--custom" v-if="tabCounts[ctab.slug] > 0">{{ tabCounts[ctab.slug] }}</span>
        </button>

        <!-- OI Range Tab — luôn hiển thị, có popup inline để nhập min/max -->
        <div class="oi-range-tab-wrapper" ref="oiRangeWrapper">
          <button
            class="new-ui-tab-btn tab-oi-range"
            :class="{ active: activeMarketTab === 'oirange', 'has-value': minOi !== null || maxOi !== null }"
            @click="onOiRangeTabClick"
          >
            <i class="fa-solid fa-chart-line"></i>
            OI Range
            <span class="tab-count-badge tab-count-badge--oi" v-if="(minOi !== null || maxOi !== null) && tabCounts.oirange > 0">{{ tabCounts.oirange }}</span>
            <!-- Icon edit khi đã có giá trị -->
            <span
              v-if="minOi !== null || maxOi !== null"
              class="oi-range-edit"
              @click.stop="toggleOiRangePopup"
              title="Edit OI Range"
            >
              <i class="fa-solid fa-pen"></i>
            </span>
            <!-- Icon X để clear -->
            <span
              v-if="minOi !== null || maxOi !== null"
              class="oi-range-clear"
              @click.stop="clearOiRange"
              title="Clear OI filter"
            >
              <i class="fa-solid fa-xmark"></i>
            </span>
          </button>

          <!-- Popup nhập OI Range -->
          <div class="oi-range-popup" v-if="showOiRangePopup">
            <div class="oi-range-popup-header">
              <span><i class="fa-solid fa-chart-line"></i> OI Range Filter</span>
              <button class="oi-popup-close" @click="closeOiRangePopup"><i class="fa-solid fa-xmark"></i></button>
            </div>
            <div class="oi-range-popup-body">
              <div class="oi-range-input-group">
                <label>Min OI (M)</label>
                <input
                  v-model.number="oiMinInput"
                  type="number"
                  placeholder="e.g. 5"
                  class="oi-range-input"
                  @keyup.enter="applyOiRange"
                />
              </div>
              <span class="oi-range-sep">—</span>
              <div class="oi-range-input-group">
                <label>Max OI (M)</label>
                <input
                  v-model.number="oiMaxInput"
                  type="number"
                  placeholder="e.g. 50"
                  class="oi-range-input"
                  @keyup.enter="applyOiRange"
                />
              </div>
            </div>
            <div class="oi-range-popup-footer">
              <button class="oi-popup-apply" @click="applyOiRange" :disabled="isSearching">
                <i class="fa-solid fa-magnifying-glass"></i> Apply
              </button>
              <button class="oi-popup-reset" @click="clearOiRange" v-if="minOi !== null || maxOi !== null">
                <i class="fa-solid fa-rotate-left"></i> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 5. Symbols Table Grid -->
      <div class="new-ui-table-wrapper" v-show="showTable">
        <table class="new-ui-table" :class="{ 'is-loading': isSearching }">
          <thead>
            <tr>
              <!-- Symbol Header -->
              <th class="col-grouped-header" style="cursor: default;">
                <div class="grouped-header-col" style="align-items: flex-start;">
                  <span class="grouped-sub-title" style="cursor: default;">Symbol</span>
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'openInterest' }" @click.stop="handleHeaderClick('openInterest')">
                    OI
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'openInterest' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'openInterest' && order === 'desc' }"></i>
                    </span>
                  </span>
                </div>
              </th>

              <!-- 24h Volume (Spot / Futures) Grouped Header -->
              <th class="col-right col-grouped-header" style="cursor: default;">
                <div class="grouped-header-col">
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'volumespot' }" @click.stop="handleHeaderClick('volumespot')">
                    Vol-Spot
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'volumespot' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'volumespot' && order === 'desc' }"></i>
                    </span>
                  </span>
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'volumefutures' }" @click.stop="handleHeaderClick('volumefutures')">
                    Vol-Futures
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'volumefutures' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'volumefutures' && order === 'desc' }"></i>
                    </span>
                  </span>
                </div>
              </th>

              <!-- Market Cap / FDV Grouped Header -->
              <th class="col-right col-grouped-header" style="cursor: default;">
                <div class="grouped-header-col">
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'marketCap' }" @click.stop="handleHeaderClick('marketCap')">
                    Mc
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'marketCap' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'marketCap' && order === 'desc' }"></i>
                    </span>
                  </span>
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'fdv' }" @click.stop="handleHeaderClick('fdv')">
                    FDV
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'fdv' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'fdv' && order === 'desc' }"></i>
                    </span>
                  </span>
                </div>
              </th>

              <!-- OI/Mc + Vol/Mc Grouped Header -->
              <th class="col-right col-grouped-header" style="cursor: default;">
                <div class="grouped-header-col">
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'ratio' }" @click.stop="handleHeaderClick('ratio')">
                    OI/Mc
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'ratio' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'ratio' && order === 'desc' }"></i>
                    </span>
                  </span>
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'volmc' }" @click.stop="handleHeaderClick('volmc')">
                    Vol/Mc
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'volmc' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'volmc' && order === 'desc' }"></i>
                    </span>
                  </span>
                </div>
              </th>

              <!-- Long/Short Grouped Header: Top Pos / Top Acc / Acc -->
              <th class="col-right col-grouped-header" style="cursor: default;">
                <div class="grouped-header-col">
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'topPositions' }" @click.stop="handleHeaderClick('topPositions')">
                    Top pos
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'topPositions' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'topPositions' && order === 'desc' }"></i>
                    </span>
                  </span>
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'topAccounts' }" @click.stop="handleHeaderClick('topAccounts')">
                    Top acc
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'topAccounts' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'topAccounts' && order === 'desc' }"></i>
                    </span>
                  </span>
                  <span class="grouped-sub-title" :class="{ 'sort-active': filterType === 'accounts' }" @click.stop="handleHeaderClick('accounts')">
                    Acc
                    <span class="sort-indicator">
                      <i class="fa-solid fa-caret-up" :class="{ 'sort-active': filterType === 'accounts' && order === 'asc' }"></i>
                      <i class="fa-solid fa-caret-down" :class="{ 'sort-active': filterType === 'accounts' && order === 'desc' }"></i>
                    </span>
                  </span>
                </div>
              </th>


            </tr>
          </thead>
          <tbody>
            <router-link
              v-for="coin in displayedCoins"
              :key="coin.coinName"
              :to="{ name: 'BinanceNewUiCoin', params: { coinName: coin.coinName } }"
              custom
              v-slot="{ navigate, href }"
            >
              <tr
                :class="{ 'selected': selectedCoin && selectedCoin.coinName === coin.coinName }"
              >
                <!-- Symbol Column -->
                <td>
                  <div class="symbol-cell">
                    <!-- Fav Star -->
                    <i
                      class="fa-solid fa-star symbol-fav-star"
                      :class="{ 'active': coin.favoriteFlag === '1' || coin.favoriteFlag === 1 }"
                      @click.stop="toggleFavorite(coin)"
                    ></i>

                    <!-- Call Action Icon -->
                    <i
                      :class="[
                        (coin.isCall === '1' || coin.isCall === 1) ? 'fa-solid fa-bookmark active' : 'fa-regular fa-bookmark'
                      ]"
                      class="call-badge-btn"
                      @click.stop="toggleIsCall(coin)"
                      title="Call coin để vẽ chart"
                    ></i>

                    <!-- Symbol Text & Badge -->
                    <a :href="href" class="row-td-link flex-grow" @click="handleRowClick($event, coin, navigate)">
                      <div class="symbol-info">
                        <span class="symbol-link">
                          {{ coin.altName ? coin.coinName + '/' + coin.altName : coin.coinName }}
                        </span>
                        <div class="symbol-subtext">
                          <span v-if="coin.openInterest" class="oi-subvalue" :class="{ 'sort-active-val': filterType === 'openInterest' }">{{ formatNumber(coin.openInterest) }}</span>
                          <span v-else class="oi-subvalue">-</span>
                        </div>
                      </div>
                    </a>
                  </div>
                </td>

                <!-- 24h Volume (Spot / Futures) Column -->
                <td class="col-right">
                  <a :href="href" class="row-td-link" @click="handleRowClick($event, coin, navigate)">
                    <div v-if="coin.volumeSpot || coin.volumeFutures" class="flex flex-col">
                      <span :class="{ 'sort-active-val': filterType === 'volumespot' }">{{ coin.volumeSpot ? formatNumber(coin.volumeSpot) : '-' }}</span>
                      <span class="text-xs text-gray-400 font-normal" :class="{ 'sort-active-val': filterType === 'volumefutures' }">{{ coin.volumeFutures ? formatNumber(coin.volumeFutures) : '-' }}</span>
                    </div>
                    <span v-else>-</span>
                  </a>
                </td>

                <!-- Market Cap / FDV Column -->
                <td class="col-right">
                  <a :href="href" class="row-td-link" @click="handleRowClick($event, coin, navigate)">
                    <div v-if="coin.marketCap || coin.fdv" class="flex flex-col">
                      <span :class="{ 'sort-active-val': filterType === 'marketCap' }">{{ formatNumber(coin.marketCap) }}</span>
                      <span class="text-xs text-gray-400 font-normal" :class="{ 'sort-active-val': filterType === 'fdv' }">{{ formatNumber(coin.fdv) }}</span>
                    </div>
                    <span v-else>-</span>
                  </a>
                </td>

                <!-- OI/Mc + Vol/Mc Column (vertical) -->
                <td class="col-right">
                  <a :href="href" class="row-td-link" @click="handleRowClick($event, coin, navigate)">
                    <div class="flex flex-col">
                      <span v-if="coin.ratio !== null && coin.ratio !== undefined" class="text-positive" :class="{ 'sort-active-val': filterType === 'ratio' }">{{ formatPercent(coin.ratio) }}%</span>
                      <span v-else>-</span>
                      <span class="text-xs text-gray-400 font-normal" :class="{ 'sort-active-val': filterType === 'volmc' }">
                        <template v-if="coin.volmc !== null && coin.volmc !== undefined">{{ formatPercent(coin.volmc) }}%</template>
                        <template v-else>-</template>
                      </span>
                    </div>
                  </a>
                </td>

                <!-- Long/Short Column (vertical): Pos / Acc / Glb -->
                <td class="col-right">
                  <a :href="href" class="row-td-link" @click="handleRowClick($event, coin, navigate)">
                    <div class="flex flex-col">
                      <span v-if="coin.topPositions" :class="{ 'sort-active-val': filterType === 'topPositions' }">{{ coin.topPositions }}</span>
                      <span v-else>-</span>
                      <span class="text-xs text-gray-400 font-normal" :class="{ 'sort-active-val': filterType === 'topAccounts' }">
                        <template v-if="coin.topAccounts !== null && coin.topAccounts !== undefined && coin.topAccounts !== ''">{{ coin.topAccounts }}</template><template v-else>-</template>
                      </span>
                      <span class="text-xs text-gray-400 font-normal" :class="{ 'sort-active-val': filterType === 'accounts' }">
                        <template v-if="coin.globalAccounts !== null && coin.globalAccounts !== undefined && coin.globalAccounts !== ''">{{ coin.globalAccounts }}</template><template v-else>-</template>
                      </span>
                    </div>
                  </a>
                </td>
              </tr>
            </router-link>

            <!-- Loading state -->
            <tr v-if="isSearching">
              <td colspan="4" class="table-empty-state">
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 20px 0;">
                  <span class="loading-spinner"></span>
                  <span>Loading coins data...</span>
                </div>
              </td>
            </tr>
            <!-- Empty row state -->
            <tr v-else-if="!displayedCoins.length">
              <td colspan="4" class="table-empty-state">
                No coins match search or filters.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Exclusion Modal list manager -->
    <ExclusionList
      v-if="showExclusionList"
      :available-symbols="allAvailableCoins"
      @apply="handleExclusionApply"
      @close="showExclusionList = false"
    />

    <!-- ─── Custom Groups Modal ─────────────────────────────────────────── -->
    <Teleport to="body">
    <transition name="cg-fade">
    <div v-if="showCustomGroupModal" class="cg-overlay" @click.self="showCustomGroupModal = false">
      <transition name="cg-slide">
      <div class="cg-modal" v-if="showCustomGroupModal" :style="cgModalStyle">

        <!-- Header -->
        <div class="cg-modal-header">
          <div class="cg-header-left">
            <div class="cg-header-icon"><i class="fa-solid fa-sliders"></i></div>
            <div>
              <div class="cg-header-title">Custom Groups</div>
              <div class="cg-header-sub">Override tab phân loại coin theo ý muốn</div>
            </div>
          </div>
          <button class="cg-close-btn" @click="showCustomGroupModal = false">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Stats bar -->
        <div class="cg-stats-bar">
          <div class="cg-stat-item">
            <span class="cg-stat-num">{{ Object.keys(customGroupOverrides).length }}</span>
            <span class="cg-stat-label">Overrides</span>
          </div>
          <div class="cg-stat-divider"></div>
          <div class="cg-stat-item">
            <span class="cg-stat-num">{{ cgModalCoins.length }}</span>
            <span class="cg-stat-label">Hiển thị</span>
          </div>
          <div class="cg-stat-divider"></div>
          <div class="cg-stat-item">
            <span class="cg-stat-num">{{ cgModalSelected.length }}</span>
            <span class="cg-stat-label">Đã chọn</span>
          </div>
          <button
            v-if="Object.keys(customGroupOverrides).length > 0"
            @click="openResetConfirm(Object.keys(customGroupOverrides), 'Tất cả ' + Object.keys(customGroupOverrides).length + ' override')"
            class="cg-clear-all-btn"
          >
            <i class="fa-solid fa-rotate-left"></i> Reset tất cả
          </button>
        </div>

        <!-- Filters -->
        <div class="cg-modal-filters">
          <div class="cg-filter-tabs cg-filter-tabs-wrap">
            <button @click="cgModalSourceTab = 'all'" class="cg-filter-tab" :class="{ active: cgModalSourceTab === 'all' }">Tất cả</button>
            <button @click="cgModalSourceTab = 'spot'" class="cg-filter-tab" :class="{ active: cgModalSourceTab === 'spot' }">Spot Only</button>
            <button @click="cgModalSourceTab = 'spotfutures'" class="cg-filter-tab" :class="{ active: cgModalSourceTab === 'spotfutures' }">Spot &amp; Futures</button>
            <button @click="cgModalSourceTab = 'futures'" class="cg-filter-tab" :class="{ active: cgModalSourceTab === 'futures' }">Futures Only</button>
            <button @click="cgModalSourceTab = 'alpha'" class="cg-filter-tab" :class="{ active: cgModalSourceTab === 'alpha' }">Futures Alpha</button>
            <button
              v-for="ctab in customTabs" :key="ctab.slug"
              @click="cgModalSourceTab = ctab.slug"
              class="cg-filter-tab cg-filter-tab--custom"
              :class="{ active: cgModalSourceTab === ctab.slug }"
            >
              <i class="fa-solid fa-layer-group"></i> {{ ctab.name }}
            </button>
          </div>
          <div class="cg-search-wrap">
            <i class="fa-solid fa-magnifying-glass cg-search-icon"></i>
            <input v-model="cgModalSearch" class="cg-search" placeholder="Tìm kiếm coin..." />
            <button v-if="cgModalSearch" @click="cgModalSearch = ''" class="cg-search-clear"><i class="fa-solid fa-xmark"></i></button>
          </div>
        </div>

        <!-- List -->
        <div class="cg-modal-list">
          <!-- Header row -->
          <div class="cg-list-header">
            <span class="cg-lh-check"></span>
            <span class="cg-lh-name">Symbol</span>
            <span class="cg-lh-type">Tab hiện tại</span>
            <span class="cg-lh-override">Override sang</span>
          </div>
          <div v-for="coin in cgModalCoins" :key="coin.coinName" class="cg-modal-row" :class="{ 'cg-row-custom': customGroupOverrides[coin.coinName] }">
            <input type="checkbox" :value="coin.coinName" v-model="cgModalSelected" class="cg-checkbox" />
            <span class="cg-coin-name">
              {{ coin.coinName }}
              <span v-if="customGroupOverrides[coin.coinName]" class="cg-custom-label">
                <i class="fa-solid fa-pen"></i> custom
              </span>
            </span>
            <span class="cg-tab-badge" :class="'cg-badge-' + getEffectiveType(coin)">
              <i class="fa-solid"
                :class="{
                  'fa-circle-dot': getEffectiveType(coin) === 'spot',
                  'fa-chart-line': getEffectiveType(coin) === 'futures',
                  'fa-bolt': getEffectiveType(coin) === 'alpha'
                }"
              ></i>
              {{ {'spot':'Spot Only','futures':'Futures Only','alpha':'Futures Alpha', ...customTabLabelMap}[getEffectiveType(coin)] || '—' }}
            </span>
            <select
              @change="onSingleCoinGroupChange(coin.coinName, $event.target.value)"
              :value="customGroupOverrides[coin.coinName] || ''"
              class="cg-group-select"
            >
              <option value="">— System default</option>
              <option value="spot">Spot Only</option>
              <option value="futures">Futures Only</option>
              <option value="alpha">Futures Alpha</option>
              <template v-if="customTabs.length > 0">
                <option disabled>─── Custom Tabs ───</option>
                <option v-for="ctab in customTabs" :key="ctab.slug" :value="ctab.slug">
                  📌 {{ ctab.name }}
                </option>
              </template>
            </select>
          </div>
          <div v-if="cgModalCoins.length === 0" class="cg-empty">
            <i class="fa-solid fa-magnifying-glass-minus"></i>
            <span>Không tìm thấy coin nào</span>
          </div>
        </div>

        <!-- Bulk action bar -->
        <transition name="cg-bulk-slide">
        <div class="cg-modal-bulk" v-if="cgModalSelected.length > 0">
          <div class="cg-bulk-info">
            <span class="cg-bulk-count">{{ cgModalSelected.length }}</span>
            <span class="cg-bulk-label">coin đã chọn</span>
          </div>
          <div class="cg-bulk-divider"></div>
          <span class="cg-bulk-move">Chuyển sang:</span>
          <button @click="bulkSetGroup('spot')" class="cg-btn cg-btn-spot">
            <i class="fa-solid fa-circle-dot"></i> Spot
          </button>
          <button @click="bulkSetGroup('futures')" class="cg-btn cg-btn-futures">
            <i class="fa-solid fa-chart-line"></i> Futures
          </button>
          <button @click="bulkSetGroup('alpha')" class="cg-btn cg-btn-alpha">
            <i class="fa-solid fa-bolt"></i> Alpha
          </button>
          <!-- Dynamic custom tab buttons -->
          <button
            v-for="ctab in customTabs" :key="ctab.slug"
            @click="bulkSetGroup(ctab.slug)"
            class="cg-btn cg-btn-custom"
          >
            <i class="fa-solid fa-layer-group"></i> {{ ctab.name }}
          </button>
          <!-- Separator + Reset (danger) -->
          <div class="cg-bulk-divider cg-bulk-divider--push"></div>
          <button @click="openResetConfirm(cgModalSelected.slice(), cgModalSelected.length + ' coin đã chọn')" class="cg-btn cg-btn-reset">
            <i class="fa-solid fa-rotate-left"></i> Reset về default
          </button>
        </div>
        </transition>

        <!-- Active overrides summary -->
        <div class="cg-modal-summary" v-if="Object.keys(customGroupOverrides).length > 0">
          <div class="cg-summary-title">📌 Override đang áp dụng</div>
          <div class="cg-chips">
            <span v-for="(group, name) in customGroupOverrides" :key="name" class="cg-chip" :class="'cg-chip-' + group">
              <i class="fa-solid"
                :class="{ 'fa-circle-dot': group==='spot', 'fa-chart-line': group==='futures', 'fa-bolt': group==='alpha' }"
              ></i>
              {{ name }}
              <span @click="resetCustomGroups([name])" class="cg-chip-x" title="Reset"><i class="fa-solid fa-xmark"></i></span>
            </span>
          </div>
        </div>

      </div>
      </transition>
    </div>
    </transition>
    </Teleport>
    <!-- ─── End Custom Groups Modal ─────────────────────────────────────── -->

    <!-- ─── Reset Confirm Modal ──────────────────────────────────── -->
    <Teleport to="body">
    <transition name="cg-fade">
    <div v-if="showResetConfirm" class="cg-overlay" style="z-index: 10001;" @click.self="cancelResetConfirm">
      <transition name="cg-slide">
      <div class="cg-confirm-modal" v-if="showResetConfirm">
        <div class="cg-confirm-header">
          <div class="cg-confirm-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
          <div>
            <div class="cg-confirm-title">Xác nhận Reset</div>
            <div class="cg-confirm-sub">Thao tác này không thể hoàn tác</div>
          </div>
        </div>

        <div class="cg-confirm-body">
          <p class="cg-confirm-desc">
            Bạn sắp reset <strong>{{ pendingResetLabel }}</strong> về System default.
            Để tiếp tục, hãy nhập chữ xác nhận vào ô bên dưới:
          </p>
          <div class="cg-confirm-hint">
            Nhập: <code class="cg-confirm-code">xác nhận</code>
          </div>
          <input
            v-model="resetConfirmText"
            class="cg-confirm-input"
            placeholder="xác nhận"
            @keyup.enter="confirmResetExecute"
            @keyup.esc="cancelResetConfirm"
            autofocus
            ref="resetConfirmInput"
          />
        </div>

        <div class="cg-confirm-footer">
          <button @click="cancelResetConfirm" class="cg-confirm-btn-cancel">
            <i class="fa-solid fa-xmark"></i> Hủy
          </button>
          <button
            @click="confirmResetExecute"
            class="cg-confirm-btn-ok"
            :disabled="resetConfirmText.trim().toLowerCase() !== 'xác nhận'"
          >
            <i class="fa-solid fa-check"></i> Xác nhận Reset
          </button>
        </div>
      </div>
      </transition>
    </div>
    </transition>
    </Teleport>
    <!-- ─── Tab Manager Modal ─────────────────────────────────────── -->
    <Teleport to="body">
    <transition name="cg-fade">
    <div v-if="showTabManager" class="cg-overlay" style="z-index: 10002;" @click.self="closeTabManager">
      <transition name="cg-slide">
      <div class="cg-confirm-modal tm-modal" v-if="showTabManager">
        <div class="cg-confirm-header">
          <div class="cg-confirm-icon" style="background: rgba(102,126,234,0.12); border-color: rgba(102,126,234,0.3); color: #667eea;">
            <i class="fa-solid fa-layer-group"></i>
          </div>
          <div>
            <div class="cg-confirm-title">Quản lý Custom Tabs</div>
            <div class="cg-confirm-sub">Tạo và xóa tab tùy chỉnh</div>
          </div>
          <button class="cg-close-btn" @click="closeTabManager" style="margin-left: auto;"><i class="fa-solid fa-xmark"></i></button>
        </div>

        <!-- Create new tab -->
        <div class="tm-create-row">
          <input
            v-model="newTabName"
            class="cg-confirm-input"
            placeholder="Tên tab mới (VD: Spot ABC)"
            maxlength="30"
            @keyup.enter="createCustomTab"
            style="flex: 1;"
          />
          <button
            @click="createCustomTab"
            class="cg-confirm-btn-ok"
            :disabled="!newTabName.trim() || isCreatingTab"
            style="white-space: nowrap;"
          >
            <i class="fa-solid fa-plus"></i> Tạo tab
          </button>
        </div>

        <!-- Tab list -->
        <div class="tm-tab-list">
          <div v-if="customTabs.length === 0" class="cg-empty">
            <i class="fa-solid fa-layer-group"></i>
            <span>Chưa có custom tab nào</span>
          </div>
          <div v-for="ctab in customTabs" :key="ctab.id" class="tm-tab-row">
            <div class="tm-tab-info">
              <i class="fa-solid fa-layer-group" style="color: #667eea; font-size: 12px;"></i>
              <span class="tm-tab-name">{{ ctab.name }}</span>
              <span class="tm-tab-coin-count" v-if="ctab.coinCount > 0">
                {{ ctab.coinCount }} coin
              </span>
              <span class="tm-tab-coin-count tm-empty" v-else>trống</span>
            </div>
            <button
              @click="deleteCustomTab(ctab)"
              class="tm-delete-btn"
              :disabled="ctab.coinCount > 0"
              :title="ctab.coinCount > 0 ? 'Cần chuyển hết ' + ctab.coinCount + ' coin sang tab khác trước khi xóa' : 'Xóa tab này'"
            >
              <i class="fa-solid fa-trash-can"></i>
            </button>
          </div>
        </div>

        <div class="cg-confirm-footer">
          <button @click="closeTabManager" class="cg-confirm-btn-cancel">
            <i class="fa-solid fa-xmark"></i> Đóng
          </button>
        </div>
      </div>
      </transition>
    </div>
    </transition>
    </Teleport>
    <!-- ─── End Tab Manager Modal ─────────────────────────────────── -->

  </div>
</template>

<script>
import { formatNumber, formatPercent } from "@/utils/number";
import { METRIC_OPTIONS } from "@/constants/metrics";
import debounce from "lodash/debounce";
import BinanceService from "@/services/BinanceService";
import ExclusionList from "@/components/exclusion/ExclusionList.vue";
import { useExclusionGroupsStore } from "@/stores/exclusionGroups";
import { API_URL } from "@/config";
import '@/assets/css/binance-new-ui.css';

export default {
  name: "FavoriteBinanceNewUi",
  components: {
    ExclusionList
  },
  props: {
    selectedMetrics: {
      type: Array,
      default: () => ['inflow30d', 'inflow24h', 'fundingRate']
    }
  },
  setup() {
    const exclusionStore = useExclusionGroupsStore();
    return {
      exclusionStore,
    };
  },
  data() {
    return {
      periods: "15m",
      isMetricsOpen: false,
      metricOptions: METRIC_OPTIONS,
      coins: [],
      searchQuery: "",
      filteredCoins: [],
      loading: false,
      activeMarketTab: 'all', // Default: All Markets
      filterType: 'openInterest', // sort column key (backend parameter)
      order: 'desc', // sort direction: 'desc' | 'asc'
      selectedTypes: ['spot', 'futures', 'alpha'],
      isSearching: false,
      isRefreshing: false,
      selectedCoin: null,
      showAdvancedFilters: true,
      showExclusionList: false,
      allAvailableCoins: [],
      showDropdown: true,
      minOi: null,
      maxOi: null,
      // OI Range popup
      showOiRangePopup: false,
      oiMinInput: null,
      oiMaxInput: null,
      allDbCoins: [],
      excludedDbCoins: [], // Coins after exclusion, for stable count badges
      showTable: !this.$route.params.coinName, // Ẩn danh sách coin nếu đang xem chart của 1 coin cụ thể
      isMultiCoinOpen: false, // Trạng thái đóng/mở dropdown Multi Coin
      binanceAlphaSymbols: new Set(),
      autoRefreshTimer: null,
      // Custom Groups
      customGroupOverrides: {},    // { "XMR": "spot", "BNX": "alpha" } — global admin-set
      showCustomGroupModal: false,
      cgModalSearch: '',
      cgModalSourceTab: 'all',
      cgModalSelected: [],
      // Custom Tabs
      customTabs: [],              // [{ id, name, slug, displayOrder, coinCount }]
      showTabManager: false,
      newTabName: '',
      isCreatingTab: false,
      // Reset Confirm
      showResetConfirm: false,
      resetConfirmText: '',
      pendingResetCoins: null,     // null = all, array = specific coins
      pendingResetLabel: '',
    };
  },
  async created() {
    // Restore OI Range từ localStorage
    const saved = localStorage.getItem('btc_oi_range');
    if (saved) {
      try {
        const { min, max } = JSON.parse(saved);
        if (min !== undefined && min !== null) { this.minOi = min; this.oiMinInput = min; }
        if (max !== undefined && max !== null) { this.maxOi = max; this.oiMaxInput = max; }
      } catch (e) { /* ignore */ }
    }
    // Fetch all datasets in parallel for faster init
    await Promise.all([this.fetchAllCoins(), this.fetchExcludedDbCoins(), this.fetchBinanceAlphaCoins(), this.fetchCustomGroups(), this.fetchCustomTabs()]);
    // Run search on mount to load coins by default with metrics
    await this.applySearch();
  },
  computed: {
    metricsProxy: {
      get() {
        return this.selectedMetrics;
      },
      set(val) {
        this.$emit("metrics-changed", val);
      }
    },
    metricsLabel() {
      if (!this.selectedMetrics.length) return 'Select metrics';
      if (this.selectedMetrics.length === this.metricOptions.length) return 'All metrics';
      return this.metricOptions
        .filter(o => this.selectedMetrics.includes(o.value))
        .map(o => o.label)
        .join(', ');
    },
    displayedCoins() {
      return this.getActiveTabCoins();
    },
    activeMarketTabName() {
      const names = {
        all: 'All Markets',
        filtered: 'Filter Exclusion',
        favorites: 'Favorites',
        spot: 'Spot',
        spotfutures: 'Spot & Futures',
        futures: 'Futures',
        alpha: 'Futures Alpha',
        monitoring: 'Monitoring'
      };
      return names[this.activeMarketTab] || '';
    },
    totalActiveTabCoins() {
      return this.tabCounts[this.activeMarketTab] || 0;
    },
    tabCounts() {
      const counts = {
        all: 0,
        filtered: 0,
        favorites: 0,
        spot: 0,
        spotfutures: 0,
        futures: 0,
        alpha: 0,
        oirange: 0,
        monitoring: 0
      };

      // All, Filtered, Spot, Futures, Alpha: KHÔNG có OI filter (giống staging)
      // Chỉ tab 'oirange' mới lọc theo OI
      counts.all = this.allDbCoins.length;
      this.allDbCoins.forEach(c => {
        if (c.favoriteFlag === "1" || c.favoriteFlag === 1) {
          counts.favorites++;
        }
      });

      counts.filtered = this.excludedDbCoins.length;
      this.excludedDbCoins.forEach(c => {
        const eff = this.getEffectiveType(c);
        if (eff === 'spot')    counts.spot++;
        if (eff === 'futures') counts.futures++;
        if (eff === 'alpha')   counts.alpha++;
        // spotfutures: đếm theo type gốc (không qua custom group override)
        const type = String(c.type || '').toLowerCase();
        if (type.includes('spot') && type.includes('futures')) {
          counts.spotfutures++;
        }
      });

      // Monitoring: đếm từ allDbCoins (KHÔNG áp dụng exclusion)
      // vì mục đích tab là xem tất cả monitoring coins kể cả coins đã exclude
      this.allDbCoins.forEach(c => {
        if (c.monitoringFlag === "Y" || c.monitoringFlag === 1) {
          counts.monitoring++;
        }
      });

      // OI Range: chỉ đếm khi có giá trị OI filter, lấy từ excludedDbCoins
      const minVal = this.minOi ? this.minOi * 1000000 : null;
      const maxVal = this.maxOi ? this.maxOi * 1000000 : null;
      if (minVal !== null || maxVal !== null) {
        counts.oirange = this.excludedDbCoins.filter(c => {
          const oi = parseFloat(c.openInterest || 0);
          if (minVal !== null && oi < minVal) return false;
          if (maxVal !== null && oi > maxVal) return false;
          return true;
        }).length;
      }

      // Custom Tabs: đếm coin được gán vào từng custom tab
      this.customTabs.forEach(ctab => {
        counts[ctab.slug] = this.excludedDbCoins.filter(c =>
          this.customGroupOverrides[String(c.coinName || '').toUpperCase()] === ctab.slug
        ).length;
      });

      return counts;
    },

    exclusionGroups() {
      return [
        ...this.exclusionStore.systemGroups,
        ...this.exclusionStore.blockedGroups,
        ...this.exclusionStore.customGroups,
      ];
    },
    multiCoinList() {
      return this.allDbCoins.filter(coin => coin.isCall === "1" || coin.isCall === 1);
    },
    multiCoinCount() {
      return this.multiCoinList.length;
    },
    multiCoinLabel() {
      return `Multi Coins (${this.multiCoinCount})`;
    },
    isFilterActive() {
      return (this.searchQuery && this.searchQuery.trim() !== "") || 
             this.minOi !== null || 
             this.maxOi !== null || 
             (this.activeExclusionTag && this.activeExclusionTag !== 'all');
    },
    cgModalCoins() {
      let list = this.allDbCoins;
      const tab = this.cgModalSourceTab;
      if (tab !== 'all') {
        if (tab === 'spotfutures') {
          // Spot & Futures: coin có type gốc chứa cả spot lẫn futures
          list = list.filter(c => {
            const t = String(c.type || '').toLowerCase();
            return t.includes('spot') && t.includes('futures');
          });
        } else if (tab === 'monitoring') {
          // Monitoring: coin có monitoringFlag
          list = list.filter(c => c.monitoringFlag === 'Y' || c.monitoringFlag === 1);
        } else if (this.customTabs.some(ct => ct.slug === tab)) {
          // Custom tab: coin được gán vào tab đó qua override
          list = list.filter(c =>
            this.customGroupOverrides[String(c.coinName || '').toUpperCase()] === tab
          );
        } else {
          // spot / futures / alpha: dùng getEffectiveType
          list = list.filter(c => this.getEffectiveType(c) === tab);
        }
      }
      // Filter theo search
      if (this.cgModalSearch && this.cgModalSearch.trim()) {
        const q = this.cgModalSearch.trim().toUpperCase();
        list = list.filter(c => (c.coinName || '').toUpperCase().includes(q));
      }
      return list;
    },
    customTabLabelMap() {
      const map = {};
      this.customTabs.forEach(t => { map[t.slug] = t.name; });
      return map;
    },
    cgModalStyle() {
      // Mỗi custom tab cần ~110px, cộng vào base 660px, giới hạn 96vw
      // Các tab cố định (Tất cả, Spot Only, Spot&Futures, Futures Only, Alpha, Monitoring) ~ 580px
      const BASE_WIDTH = 680;
      const PER_CUSTOM_TAB = 115;
      const targetPx = BASE_WIDTH + this.customTabs.length * PER_CUSTOM_TAB;
      return { width: targetPx + 'px', maxWidth: '96vw' };
    },
  },
  watch: {
    '$route'(to) {
      this.showTable = !to.params.coinName;
    },
    periods(newVal) {
      this.$emit("period-selected", newVal);
    }
  },
  methods: {
    handleRowClick(event, coin, navigate) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
        return;
      }
      navigate(event);
      this.selectedCoin = coin;
      this.showTable = false;
    },
    formatNumber,
    formatPercent,
    handleOutsideClick(e) {
      if (!this.isMetricsOpen) return;
      if (e.target.closest('.new-ui-metrics-select-btn') || e.target.closest('.new-ui-metrics-dropdown-menu')) return;
      this.isMetricsOpen = false;
    },
    selectAll() {
      this.metricsProxy = this.metricOptions.map(o => o.value);
    },
    clearAll() {
      this.metricsProxy = [];
    },
    
    async toggleIsCall(coin) {
      if (this.loading) return;
      this.loading = true;
      try {
        const newIsCall = (coin.isCall === "1" || coin.isCall === 1) ? "0" : "1";
        coin.isCall = newIsCall;
        await BinanceService.updateFavorite([{
          coinName: coin.coinName,
          favoriteFlag: coin.favoriteFlag,
          isCall: coin.isCall,
        }]);

        // Cập nhật các caches cục bộ
        const targetAll = this.allDbCoins.find(c => c.coinName === coin.coinName);
        if (targetAll) {
          targetAll.isCall = newIsCall;
        }
        const targetExcluded = this.excludedDbCoins.find(c => c.coinName === coin.coinName);
        if (targetExcluded) {
          targetExcluded.isCall = newIsCall;
        }
        const targetCoins = this.coins.find(c => c.coinName === coin.coinName);
        if (targetCoins) {
          targetCoins.isCall = newIsCall;
        }

        this.$toast.open({
          message: `${coin.coinName} ${newIsCall === "1" ? "đã được thêm vào" : "đã xóa khỏi"} list vẽ chart!`,
          type: "success",
          position: "top-right"
        });

        // Tải lại dữ liệu và đồng bộ lên cha
        await this.applySearch();
      } catch (e) {
        console.error("Error toggling isCall:", e);
        this.$toast.open({
          message: "Lỗi cập nhật trạng thái call!",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    async handleCallMultiInDropdown() {
      this.isMultiCoinOpen = false;
      this.selectedCoin = null;
      this.showTable = false;
      this.$emit("refresh-clicked", this.periods);
    },
    
    async handleRefresh() {
      if (this.isSearching || this.isRefreshing) return;
      this.isRefreshing = true;
      this.isSearching = true;
      try {
        await Promise.all([this.fetchAllCoins(), this.fetchExcludedDbCoins(), this.fetchBinanceAlphaCoins(), this.fetchCustomGroups(), this.fetchCustomTabs()]);
        await this.applySearch();
        this.$emit("refresh-clicked", this.periods);
      } finally {
        this.isSearching = false;
        this.isRefreshing = false;
      }
    },

    async handleTabClick(tab) {
      this.activeMarketTab = tab;

      // Check if it's a custom tab
      const isCustomTab = this.customTabs.some(t => t.slug === tab);
      if (isCustomTab) {
        this.selectedTypes = ['spot', 'futures', 'alpha'];
        await this.applySearch();
        return;
      }

      // Map tabs to selected types and search
      if (tab === 'all' || tab === 'favorites') {
        this.selectedTypes = ['spot', 'futures', 'alpha'];
        this.filterType = ''; // Clear sort → backend returns all coins unsorted
      } else if (tab === 'filtered') {
        this.selectedTypes = ['spot', 'futures', 'alpha'];
      } else if (tab === 'spot') {
        this.selectedTypes = ['spot', 'futures'];
        if (['openInterest', 'ratio', 'topPositions', 'topAccounts', 'accounts', 'volumefutures'].includes(this.filterType)) {
          this.filterType = 'marketCap';
        }
      } else if (tab === 'spotfutures') {
        // Spot & Futures: giao thoa của Spot và Futures
        this.selectedTypes = ['spot', 'futures', 'alpha'];
      } else if (tab === 'futures') {
        this.selectedTypes = ['futures'];
        if (['marketCap', 'fdv', 'volmc', 'volumespot'].includes(this.filterType)) {
          this.filterType = 'openInterest';
        }
      } else if (tab === 'alpha') {
        this.selectedTypes = ['alpha'];
        if (this.filterType !== 'marketCap' && this.filterType !== 'fdv' && this.filterType !== 'volumespot') {
          this.filterType = 'marketCap';
        }
      } else if (tab === 'oirange') {
        // OI Range: lấy coin từ Filter Exclusion, lọc theo OI range
        this.selectedTypes = ['spot', 'futures', 'alpha'];
        if (!this.filterType || !['openInterest', 'ratio', 'topPositions', 'topAccounts', 'accounts'].includes(this.filterType)) {
          this.filterType = 'openInterest'; // Default sort by OI
        }
      } else if (tab === 'monitoring') {
        // Monitoring: lấy tất cả types, dùng exclusion
        this.selectedTypes = ['spot', 'futures', 'alpha'];
      }

      await this.applySearch();
    },


    async handleHeaderClick(field) {


      // Backend-side sorting
      if (this.filterType === field) {
        this.order = this.order === 'desc' ? 'asc' : 'desc';
      } else {
        this.filterType = field;
        this.order = 'desc';
      }

      await this.applySearch();
    },

    getActiveTabCoins() {
      let list = this.filteredCoins;

      // Tab All: KHÔNG apply custom group override — hiện đầy đủ như hệ thống
      if (this.activeMarketTab === 'all') return list;

      // Tab Favorites: lọc theo favorite, KHÔNG apply custom group
      if (this.activeMarketTab === 'favorites') {
        return list.filter(c => c.favoriteFlag === "1" || c.favoriteFlag === 1);
      }

      // spotfutures: dùng type gốc — tab này không áp dụng custom group override
      if (this.activeMarketTab === 'spotfutures') {
        return list.filter(c => {
          const t = String(c.type || '').toLowerCase();
          return t.includes('spot') && t.includes('futures');
        });
      }

      // monitoring: filter theo monitoringFlag — không qua custom group override
      if (this.activeMarketTab === 'monitoring') {
        return list.filter(c => c.monitoringFlag === "Y" || c.monitoringFlag === 1);
      }

      // Spot / Futures / Alpha: dùng getEffectiveType() để apply custom group override
      if (this.activeMarketTab === 'spot') {
        return list.filter(c => this.getEffectiveType(c) === 'spot');
      }
      if (this.activeMarketTab === 'futures') {
        return list.filter(c => this.getEffectiveType(c) === 'futures');
      }
      if (this.activeMarketTab === 'alpha') {
        return list.filter(c => this.getEffectiveType(c) === 'alpha');
      }

      // Custom tab: lọc coin có customGroupOverrides[coin] === slug
      const customTab = this.customTabs.find(t => t.slug === this.activeMarketTab);
      if (customTab) {
        return list.filter(c =>
          this.customGroupOverrides[String(c.coinName || '').toUpperCase()] === customTab.slug
        );
      }

      // oirange, filtered, all: giữ nguyên — không lọc theo tab type
      return list;
    },

    emitCoinsLoaded() {
      const activeTabCoins = this.getActiveTabCoins();
      this.$emit("coins-loaded", { coins: activeTabCoins, fromFilter: this.isFilterActive });
    },

    async fetchCoins(silent = false) {
      try {
        const isOiRangeTab = this.activeMarketTab === 'oirange';
        // OI Range dùng exclusion (như Filter Exclusion)
        const useExclusion = ['filtered', 'spot', 'spotfutures', 'futures', 'alpha', 'oirange'].includes(this.activeMarketTab);

        const params = {
          excludeTagEnabled: useExclusion,
          // OI Range: force filterType='openInterest' để backend sort theo OI
          // Các tab khác: dùng filterType hiện tại
          filterType: isOiRangeTab
            ? (this.filterType || 'openInterest')
            : (this.filterType || undefined),
          order: this.order,
          types: 'spot,futures,alpha',
          period: this.periods,
          // Chỉ gửi OI params khi ở tab oirange
          // Các tab khác không bị ảnh hưởng bởi OI filter
          minOi: isOiRangeTab && this.minOi ? this.minOi * 1000000 : null,
          maxOi: isOiRangeTab && this.maxOi ? this.maxOi * 1000000 : null,
        };

        const data = await BinanceService.getCoins(params);

        this.coins = data.map(coin => ({
          ...coin,
          favoriteFlag: coin.favoriteFlag || "0",
          isCall: coin.isCall || "0",
        }));

        if (this.searchQuery && this.searchQuery.trim()) {
          this.debouncedSearch();
        } else {
          this.filteredCoins = this.coins;
          this.emitCoinsLoaded();
        }
        return data;
      } catch (error) {
        console.error("Error fetching coins in New UI:", error);
        if (!silent) {
          this.$toast.open({
            message: "Unable to retrieve coin list.",
            type: "error",
            position: "top-right"
          });
        }
        return [];
      }
    },

    debouncedSearch: debounce(function () {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        this.filteredCoins = this.coins;
      } else {
        const exactMatch = [];
        const startsWithMatch = [];
        const containsMatch = [];

        for (const c of this.coins) {
          const name = c.coinName.toLowerCase();
          const alt = (c.altName || '').toLowerCase();
          const combined = alt ? name + '/' + alt : name;

          // Kiểm tra match trên coinName, altName, hoặc dạng kết hợp (RAY/RAYSOL)
          const isExact = name === query || alt === query || combined === query;
          const isStartsWith = !isExact && (name.startsWith(query) || alt.startsWith(query) || combined.startsWith(query));
          const isContains = !isExact && !isStartsWith && (name.includes(query) || alt.includes(query) || combined.includes(query));

          if (isExact) exactMatch.push(c);
          else if (isStartsWith) startsWithMatch.push(c);
          else if (isContains) containsMatch.push(c);
        }
        this.filteredCoins = [...exactMatch, ...startsWithMatch, ...containsMatch];
      }
      this.emitCoinsLoaded();
    }, 300),

    async applySearch() {
      this.isSearching = true;
      try {
        await this.fetchCoins(true);
      } finally {
        this.isSearching = false;
      }
    },

    handleRefreshClick() {
      if (this.isSearching) return;
      if (this.$route.params.coinName) {
        this.$emit("refresh-clicked", {
          coin: { coinName: this.$route.params.coinName },
          period: this.periods
        });
      } else {
        this.$emit("refresh-clicked", this.periods);
      }
    },

    onOiRangeTabClick() {
      if (this.minOi !== null || this.maxOi !== null) {
        // Đã có giá trị → chuyển sang tab và search
        this.handleTabClick('oirange');
      } else {
        // Chưa có giá trị → mở popup
        this.showOiRangePopup = true;
      }
    },

    toggleOiRangePopup() {
      this.oiMinInput = this.minOi;
      this.oiMaxInput = this.maxOi;
      this.showOiRangePopup = !this.showOiRangePopup;
    },

    closeOiRangePopup() {
      this.showOiRangePopup = false;
    },

    async applyOiRange() {
      this.minOi = this.oiMinInput !== '' && this.oiMinInput !== null ? Number(this.oiMinInput) : null;
      this.maxOi = this.oiMaxInput !== '' && this.oiMaxInput !== null ? Number(this.oiMaxInput) : null;
      // Lưu vào localStorage
      localStorage.setItem('btc_oi_range', JSON.stringify({ min: this.minOi, max: this.maxOi }));
      this.showOiRangePopup = false;
      await this.handleTabClick('oirange');
    },

    async clearOiRange() {
      this.minOi = null;
      this.maxOi = null;
      this.oiMinInput = null;
      this.oiMaxInput = null;
      this.showOiRangePopup = false;
      localStorage.removeItem('btc_oi_range');
      this.activeMarketTab = 'all';
      await this.applySearch();
    },

    async clearSearch() {
      this.searchQuery = "";
      this.filterType = "openInterest";
      this.order = "desc";
      this.limit = 10;
      this.minOi = null;
      this.maxOi = null;
      this.oiMinInput = null;
      this.oiMaxInput = null;
      localStorage.removeItem('btc_oi_range');
      this.periods = "15m";
      this.selectedTypes = ['spot', 'futures', 'alpha'];
      // Nếu đang ở tab oirange mà xoá OI filter → reset về all
      if (this.activeMarketTab === 'oirange') {
        this.activeMarketTab = 'all';
      }
      this.activeExclusionTag = 'all';
      this.selectedCoin = null;
      this.$emit("clear-single-coin-search");
      await this.applySearch();
    },

    async toggleFavorite(coin) {
      if (this.loading) return;
      this.loading = true;
      try {
        const newFav = (coin.favoriteFlag === "1" || coin.favoriteFlag === 1) ? "0" : "1";
        coin.favoriteFlag = newFav;
        await BinanceService.updateFavorite([{
          coinName: coin.coinName,
          favoriteFlag: coin.favoriteFlag,
          isCall: coin.isCall,
        }]);
        // Update local counts cache
        const target = this.allDbCoins.find(c => c.coinName === coin.coinName);
        if (target) {
          target.favoriteFlag = newFav;
        }

        this.$toast.open({
          message: `${coin.coinName} favorite updated.`,
          type: "success",
          position: "top-right"
        });
        // re-sync state
        await this.applySearch();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },



    async fetchAllCoins() {
      try {
        const data = await BinanceService.getCoins();
        this.allDbCoins = data.map(coin => ({
          ...coin,
          favoriteFlag: coin.favoriteFlag || "0",
          isCall: coin.isCall || "0",
        }));
      } catch (error) {
        console.error("Error fetching all coins:", error);
      }
    },

    async fetchExcludedDbCoins() {
      try {
        const data = await BinanceService.getCoins({ excludeTagEnabled: true });
        this.excludedDbCoins = data.map(coin => ({
          ...coin,
          favoriteFlag: coin.favoriteFlag || "0",
          isCall: coin.isCall || "0",
        }));
      } catch (error) {
        console.error("Error fetching excluded coins:", error);
      }
    },

    async fetchBinanceAlphaCoins() {
      try {
        const response = await fetch(`${API_URL}/binance-alpha/get-coins`);
        if (!response.ok) return;
        const data = await response.json();
        if (Array.isArray(data)) {
          const symbols = new Set();
          data.forEach(item => {
            if (item && item.symbol) {
              symbols.add(item.symbol.toUpperCase());
            }
          });
          this.binanceAlphaSymbols = symbols;
        }
      } catch (error) {
        console.error("Error fetching Binance Alpha coins:", error);
      }
    },

    // ─── Custom Groups ─────────────────────────────────────────────────────────

    // Xác định tab effective của coin sau khi apply custom group override
    getEffectiveType(coin) {
      const name = String(coin.coinName || '').toUpperCase();
      // 1. Custom group override (admin-set, global) — ưu tiên cao nhất
      if (this.customGroupOverrides[name]) {
        return this.customGroupOverrides[name]; // "spot" | "futures" | "alpha"
      }
      // 2. Fallback: GIỐNG HOÀN TOÀN logic gốc — không thay đổi hành vi cũ
      const t = String(coin.type || '').toLowerCase();
      if (t.includes('spot') && !t.includes('futures')) return 'spot';
      if (t.includes('futures') && !t.includes('spot') && !t.includes('alpha')
          && !this.isBinanceAlphaCoin(name)) return 'futures';
      if (t.includes('alpha')) return 'alpha';
      // 'spot futures' hoặc type khác: không thuộc tab cụ thể nào
      // (coin xuất hiện ở All tab nhưng không có trong Spot/Futures/Alpha count)
      return null;
    },

    async fetchCustomGroups() {
      try {
        const res = await fetch(`${API_URL}/binance/custom-group`);
        if (!res.ok) return;
        const data = await res.json();
        const map = {};
        data.forEach(item => {
          if (item.coinName) map[item.coinName.toUpperCase()] = item.customGroup;
        });
        this.customGroupOverrides = map;
      } catch (e) {
        console.error('Error fetching custom groups:', e);
      }
    },

    async fetchCustomTabs() {
      try {
        const res = await fetch(`${API_URL}/binance/custom-tab`);
        if (!res.ok) return;
        this.customTabs = await res.json();
      } catch (e) {
        console.error('Error fetching custom tabs:', e);
      }
    },

    async createCustomTab() {
      const name = this.newTabName.trim();
      if (!name || this.isCreatingTab) return;
      this.isCreatingTab = true;
      try {
        const res = await fetch(`${API_URL}/binance/custom-tab`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        if (!res.ok) {
          const msg = await res.text();
          this.$toast?.open({ message: 'Lỗi tạo tab: ' + msg, type: 'error', position: 'top-right' });
          return;
        }
        const newTab = await res.json();
        this.customTabs.push(newTab);
        this.newTabName = '';
        this.$toast?.open({ message: `Đã tạo tab "${newTab.name}"!`, type: 'success', position: 'top-right' });
      } catch (e) {
        console.error('Error creating custom tab:', e);
        this.$toast?.open({ message: 'Lỗi tạo custom tab!', type: 'error', position: 'top-right' });
      } finally {
        this.isCreatingTab = false;
      }
    },

    async deleteCustomTab(ctab) {
      if (ctab.coinCount > 0) {
        this.$toast?.open({
          message: `Không thể xóa: tab "${ctab.name}" còn ${ctab.coinCount} coin. Vui lòng chuyển hết coin sang tab khác trước.`,
          type: 'warning', position: 'top-right'
        });
        return;
      }
      try {
        const res = await fetch(`${API_URL}/binance/custom-tab/${ctab.id}`, { method: 'DELETE' });
        if (!res.ok) {
          const msg = await res.text();
          this.$toast?.open({ message: 'Lỗi xóa tab: ' + msg, type: 'error', position: 'top-right' });
          return;
        }
        this.customTabs = this.customTabs.filter(t => t.id !== ctab.id);
        // Nếu đang ở tab bị xóa, chuyển về All
        if (this.activeMarketTab === ctab.slug) {
          this.handleTabClick('all');
        }
        this.$toast?.open({ message: `Đã xóa tab "${ctab.name}"!`, type: 'success', position: 'top-right' });
      } catch (e) {
        console.error('Error deleting custom tab:', e);
        this.$toast?.open({ message: 'Lỗi xóa custom tab!', type: 'error', position: 'top-right' });
      }
    },

    closeTabManager() {
      this.showTabManager = false;
      this.newTabName = '';
    },


    async saveCustomGroups(coins, targetGroup) {
      try {
        await fetch(`${API_URL}/binance/custom-group`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ coins, customGroup: targetGroup }),
        });
        const map = { ...this.customGroupOverrides };
        coins.forEach(name => { map[name.toUpperCase()] = targetGroup; });
        this.customGroupOverrides = map;
      } catch (e) {
        console.error('Error saving custom groups:', e);
        this.$toast?.open({ message: 'Lỗi lưu Custom Group!', type: 'error', position: 'top-right' });
      }
    },

    async resetCustomGroups(coins) {
      try {
        await fetch(`${API_URL}/binance/custom-group`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ coins }),
        });
        const map = { ...this.customGroupOverrides };
        coins.forEach(name => delete map[name.toUpperCase()]);
        this.customGroupOverrides = map;
      } catch (e) {
        console.error('Error resetting custom groups:', e);
        this.$toast?.open({ message: 'Lỗi reset Custom Group!', type: 'error', position: 'top-right' });
      }
    },

    async bulkSetGroup(targetGroup) {
      if (!this.cgModalSelected.length) return;
      await this.saveCustomGroups(this.cgModalSelected, targetGroup);
      this.cgModalSelected = [];
    },

    openResetConfirm(coins, label) {
      this.pendingResetCoins = coins;
      this.pendingResetLabel = label;
      this.resetConfirmText = '';
      this.showResetConfirm = true;
      this.$nextTick(() => {
        if (this.$refs.resetConfirmInput) this.$refs.resetConfirmInput.focus();
      });
    },
    cancelResetConfirm() {
      this.showResetConfirm = false;
      this.resetConfirmText = '';
      this.pendingResetCoins = null;
      this.pendingResetLabel = '';
    },
    async confirmResetExecute() {
      if (this.resetConfirmText.trim().toLowerCase() !== 'xác nhận') return;
      const coins = this.pendingResetCoins;
      this.cancelResetConfirm();
      await this.resetCustomGroups(coins);
      this.cgModalSelected = [];
    },
    async bulkReset() {
      if (!this.cgModalSelected.length) return;
      await this.resetCustomGroups(this.cgModalSelected);
      this.cgModalSelected = [];
    },

    async onSingleCoinGroupChange(coinName, targetGroup) {
      if (!targetGroup) {
        await this.resetCustomGroups([coinName]);
      } else {
        await this.saveCustomGroups([coinName], targetGroup);
      }
    },

    // ─── End Custom Groups ─────────────────────────────────────────────────────

    isBinanceAlphaCoin(coinName) {
      if (!coinName) return false;
      const upper = String(coinName).toUpperCase().trim();

      // Danh sách các coin có niêm yết ở Binance Alpha cần loại trừ khỏi Futures Only
      const EXCLUDED_FUTURES_ONLY_ALPHA_SYMBOLS = new Set([
        'BROCCOLIF3B', '1000000BOB', 'PROMPT', '1000000MOG',
        'TRUST', 'SAFE', 'ATH', 'FLUID', 'H', 'ICNT',
        'ZETA', 'AIN', 'DRIFT'
      ]);

      const clean = upper.replace(/USDT$/i, "");
      if (EXCLUDED_FUTURES_ONLY_ALPHA_SYMBOLS.has(upper) || EXCLUDED_FUTURES_ONLY_ALPHA_SYMBOLS.has(clean)) {
        return true;
      }

      if (!this.binanceAlphaSymbols || this.binanceAlphaSymbols.size === 0) return false;
      if (this.binanceAlphaSymbols.has(upper)) return true;
      for (const suffix of ['USDT', 'USDC', 'BUSD']) {
        if (upper.endsWith(suffix) && upper.length > suffix.length) {
          const base = upper.slice(0, -suffix.length);
          if (this.binanceAlphaSymbols.has(base)) return true;
        }
      }
      return false;
    },

    async openExclusionManager() {
      const loading = this.$loading.show();
      try {
        const data = await BinanceService.getCoins({
          excludeTagEnabled: false,
          types: 'spot,futures,alpha'
        });
        this.allAvailableCoins = data || [];
        this.showExclusionList = true;
      } catch (error) {
        console.error("Lỗi khi tải toàn bộ coins cho Exclusion List:", error);
      } finally {
        loading.hide();
      }
    },

    async handleExclusionApply() {
      await this.fetchExcludedDbCoins();
      await this.applySearch();
    },

    handleClickOutside(event) {
      const dropdown = this.$el.querySelector(".new-ui-multicoin-dropdown");
      if (dropdown && !dropdown.contains(event.target)) {
        this.isMultiCoinOpen = false;
      }
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      this.autoRefreshTimer = setInterval(() => {
        this.silentAutoRefresh();
      }, 60000); // Tự động cập nhật ngầm mỗi 1 phút
    },

    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },

    async silentAutoRefresh() {
      if (this.isSearching || this.isRefreshing) return;
      try {
        await this.fetchCoins(true);
      } catch (err) {
        console.error("Auto refresh coins failed:", err);
      }
    },

  },
  mounted() {
    this.exclusionStore.fetchGroups();
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("click", this.handleOutsideClick);
    // OI Range: close popup when clicking outside
    this._oiClickOutside = (e) => {
      if (this.$refs.oiRangeWrapper && !this.$refs.oiRangeWrapper.contains(e.target)) {
        this.showOiRangePopup = false;
      }
    };
    document.addEventListener('click', this._oiClickOutside);
    this.startAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener('click', this._oiClickOutside);
  }
};
</script>

<style scoped>
/* =============================================================
   Scoped overrides to defeat global index.css rules:
   - .dark-mode button:not(.dark-mode-btn):not(.active) { bg: #2b3139 }
   - .dark-mode input, .dark-mode select { bg: #2b3139 }
   - button[class*="tab-"] styles from the app tabs
   These global rules would override our always-dark Binance theme.
   ============================================================= */

/* Force all text inside our container to use Binance colors */
.binance-new-ui-container,
.binance-new-ui-container span,
.binance-new-ui-container label,
.binance-new-ui-container p {
  color: #EAECEF !important;
}

body:not(.dark-mode) .binance-new-ui-container,
body:not(.dark-mode) .binance-new-ui-container span,
body:not(.dark-mode) .binance-new-ui-container label,
body:not(.dark-mode) .binance-new-ui-container p {
  color: #1E2329 !important;
}

/* FDV subtext */
.binance-new-ui-container .text-gray-400 {
  color: #5E6673 !important;
}

body:not(.dark-mode) .binance-new-ui-container .text-gray-400 {
  color: #707A8A !important;
}

/* Loading spinner */
.loading-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(234, 236, 239, 0.15);
  border-top-color: #F0B90B;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.tab-count-badge {
  background: rgba(255, 255, 255, 0.12) !important;
  color: #848E9C !important;
  border-radius: 10px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 5px;
  display: inline-block;
  vertical-align: middle;
  transition: all 0.2s ease;
  line-height: 1.4;
}

body:not(.dark-mode) .tab-count-badge {
  background: #EAECEF !important;
  color: #474F59 !important;
  font-weight: 600 !important;
}

.new-ui-tab-btn.active .tab-count-badge {
  background: #F0B90B !important;
  color: #181A20 !important;
}

body:not(.dark-mode) .new-ui-tab-btn.active .tab-count-badge {
  background: #F0B90B !important;
  color: #FFFFFF !important;
}

/* OI Range Tab — phân biệt bằng màu tím */
.new-ui-tab-btn.tab-oi-range {
  border-color: #7B61FF;
  color: #A78BFA;
}

.new-ui-tab-btn.tab-oi-range:hover {
  background: rgba(123, 97, 255, 0.08);
  border-color: #A78BFA;
  color: #C4B5FD;
}

.new-ui-tab-btn.tab-oi-range.active {
  background: linear-gradient(135deg, #7B61FF22, #A78BFA22);
  border-color: #A78BFA;
  color: #C4B5FD;
}

.tab-count-badge--oi {
  background: #7B61FF !important;
  color: #fff !important;
}

.new-ui-tab-btn.tab-oi-range.active .tab-count-badge--oi {
  background: #A78BFA !important;
  color: #181A20 !important;
}

body:not(.dark-mode) .new-ui-tab-btn.tab-oi-range {
  border-color: #7B61FF;
  color: #7B61FF;
}

body:not(.dark-mode) .new-ui-tab-btn.tab-oi-range.active {
  background: rgba(123, 97, 255, 0.06);
  border-color: #7B61FF;
  color: #6D4AFF;
}

.oi-range-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(123, 97, 255, 0.25);
  color: #A78BFA;
  font-size: 9px;
  margin-left: 5px;
  vertical-align: middle;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.oi-range-clear:hover {
  background: rgba(239, 68, 68, 0.3);
  color: #FCA5A5;
}

body:not(.dark-mode) .oi-range-clear {
  background: rgba(123, 97, 255, 0.15);
  color: #7B61FF;
}

body:not(.dark-mode) .oi-range-clear:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #DC2626;
}

/* === OI Range Tab Wrapper & Popup === */
.oi-range-tab-wrapper {
  position: relative;
  display: inline-block;
}

/* Icon edit (pencil) */
.oi-range-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgba(123, 97, 255, 0.2);
  color: #A78BFA;
  font-size: 8px;
  margin-left: 4px;
  vertical-align: middle;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.oi-range-edit:hover {
  background: rgba(123, 97, 255, 0.4);
  color: #C4B5FD;
}

/* Popup card */
.oi-range-popup {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: #1E2130;
  border: 1px solid #7B61FF44;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(123, 97, 255, 0.15);
  z-index: 200;
  animation: oi-popup-in 0.18s ease;
  overflow: hidden;
}

@keyframes oi-popup-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.oi-range-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px;
  border-bottom: 1px solid rgba(123, 97, 255, 0.2);
  font-size: 12px;
  font-weight: 600;
  color: #A78BFA;
  letter-spacing: 0.02em;
}

.oi-popup-close {
  background: none;
  border: none;
  color: #5E6673;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s;
}

.oi-popup-close:hover {
  color: #EF4444;
}

.oi-range-popup-body {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 14px;
}

.oi-range-input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.oi-range-input-group label {
  font-size: 10px;
  color: #5E6673;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.oi-range-input {
  background: #252836;
  border: 1px solid #2D3148;
  border-radius: 7px;
  color: #E2E8F0;
  font-size: 13px;
  padding: 7px 10px;
  width: 100%;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.oi-range-input:focus {
  border-color: #7B61FF;
  box-shadow: 0 0 0 2px rgba(123, 97, 255, 0.15);
}

.oi-range-input::placeholder {
  color: #3D4460;
}

.oi-range-sep {
  color: #3D4460;
  font-size: 14px;
  padding-bottom: 10px;
  flex-shrink: 0;
}

.oi-range-popup-footer {
  display: flex;
  gap: 8px;
  padding: 0 14px 14px;
}

.oi-popup-apply {
  flex: 1;
  background: linear-gradient(135deg, #7B61FF, #9D4EDD);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.oi-popup-apply:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.oi-popup-apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.oi-popup-reset {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 8px;
  color: #FCA5A5;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.oi-popup-reset:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* Light mode */
body:not(.dark-mode) .oi-range-popup {
  background: #FFFFFF;
  border-color: #7B61FF33;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(123, 97, 255, 0.1);
}

body:not(.dark-mode) .oi-range-popup-header {
  border-bottom-color: rgba(123, 97, 255, 0.15);
  color: #6D4AFF;
}

body:not(.dark-mode) .oi-range-input {
  background: #F8F9FA;
  border-color: #E0E3EA;
  color: #1A202C;
}

body:not(.dark-mode) .oi-range-input:focus {
  border-color: #7B61FF;
}

body:not(.dark-mode) .oi-range-input::placeholder {
  color: #B0B8C4;
}

body:not(.dark-mode) .oi-range-input-group label {
  color: #8896A8;
}

body:not(.dark-mode) .oi-range-sep {
  color: #C0C8D4;
}

body:not(.dark-mode) .oi-popup-close:hover {
  color: #DC2626;
}

/* has-value state: highlight tab khi đã set giá trị */
.new-ui-tab-btn.tab-oi-range.has-value {
  border-color: #A78BFA;
}

.tab-exclusion-hint {
  font-size: 10px;
  color: #5E6673;
  font-weight: 400;
  font-style: italic;
  margin-left: 2px;
}

body:not(.dark-mode) .tab-exclusion-hint {
  color: #AEB4BC;
}

.new-ui-tab-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 13px;
  color: #848E9C !important;
}

body:not(.dark-mode) .new-ui-tab-summary {
  background: rgba(0, 0, 0, 0.01) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
  color: #707A8A !important;
}

.new-ui-tab-summary strong {
  color: #F0B90B !important;
}

/* OI sub-value under symbol name */
.oi-subvalue {
  font-size: 11px;
  color: #848E9C !important;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
}

body:not(.dark-mode) .oi-subvalue {
  color: #707A8A !important;
}

/* ── Alignment & Metrics Dropdown Right Canh ── */
.binance-new-ui-container {
  overflow: visible !important;
  position: relative !important;
  z-index: 99 !important;
}

.drawer-field.relative {
  position: relative !important;
}

.drawer-input,
.new-ui-metrics-select-btn {
  box-sizing: border-box !important;
  height: 38px !important;
  line-height: 36px !important;
  border-radius: 6px !important;
  border: 1px solid #363C4E !important;
  padding: 0 10px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  outline: none !important;
  margin: 0 !important;
}

body:not(.dark-mode) .drawer-input,
body:not(.dark-mode) .new-ui-metrics-select-btn {
  background-color: #FFFFFF !important;
  border-color: #D1D4DC !important;
  color: #1E2329 !important;
}

select.drawer-input {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23848E9C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
  background-repeat: no-repeat !important;
  background-position: right 10px center !important;
  background-size: 16px !important;
  padding-right: 30px !important;
}

body:not(.dark-mode) select.drawer-input {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23474F59' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
}

.new-ui-metrics-dropdown-menu {
  position: absolute !important;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  width: 240px !important;
  z-index: 100 !important;
  margin-top: 6px !important;
  background-color: #1e2230 !important;
  border: 1px solid #363C4E !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
  overflow: hidden !important;
}

body:not(.dark-mode) .new-ui-metrics-dropdown-menu {
  background-color: #ffffff !important;
  border-color: #EAECEF !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
}

.row-td-link {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  color: inherit !important;
  text-decoration: none !important;
}

.col-right .row-td-link {
  align-items: flex-end;
}

/* ─── Custom Groups — Trigger Button ──────────────────────── */
.cg-action-btns {
  display: flex; gap: 8px; align-items: center;
}
.cg-main-trigger-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #2d3748 0%, #1e2533 100%);
  border: 1px solid #4a5568;
  border-radius: 7px;
  color: #cbd5e0;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.cg-main-trigger-btn:hover {
  background: linear-gradient(135deg, #3d4a62 0%, #2a3348 100%);
  border-color: #667eea;
  color: #e2e8f0;
  box-shadow: 0 0 0 2px rgba(102,126,234,0.2);
}
.cg-main-trigger-btn i { color: #667eea; font-size: 13px; }
.cg-main-count {
  display: inline-flex; align-items: center; justify-content: center;
  background: #f59e0b; color: #1a1d23;
  border-radius: 10px; padding: 1px 6px;
  font-size: 10px; font-weight: 700; min-width: 18px;
}
.tab-manager-btn i { color: #a78bfa !important; }
.tab-manager-btn:hover {
  border-color: #a78bfa !important;
  box-shadow: 0 0 0 2px rgba(102,126,234,0.2) !important;
}
.tm-tab-count-badge {
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(102,126,234,0.3); color: #c4b5fd;
  border-radius: 10px; padding: 1px 7px;
  font-size: 10px; font-weight: 700; min-width: 18px;
}

/* ─── Custom Groups — Overlay & Modal ─────────────────────── */
.cg-fade-enter-active, .cg-fade-leave-active { transition: opacity 0.2s ease; }
.cg-fade-enter-from, .cg-fade-leave-to { opacity: 0; }
.cg-slide-enter-active, .cg-slide-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
.cg-slide-enter-from, .cg-slide-leave-to { transform: translateY(-16px); opacity: 0; }
.cg-bulk-slide-enter-active, .cg-bulk-slide-leave-active { transition: all 0.2s ease; max-height: 60px; overflow: hidden; }
.cg-bulk-slide-enter-from, .cg-bulk-slide-leave-to { max-height: 0; opacity: 0; }

.cg-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
}
.cg-modal {
  background: #13161d;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  width: 660px; max-width: 96vw; max-height: calc(100vh - 76px);
  display: flex; flex-direction: column; overflow: hidden;
  color: #e2e8f0;
  box-shadow: 0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(102,126,234,0.1);
  transition: width 0.25s ease;
}

/* Header */
.cg-modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 22px 16px;
  background: linear-gradient(180deg, #1a1f2e 0%, #13161d 100%);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.cg-header-left { display: flex; align-items: center; gap: 12px; }
.cg-header-icon {
  width: 36px; height: 36px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; color: #fff;
  box-shadow: 0 4px 12px rgba(102,126,234,0.35);
}
.cg-header-title { font-size: 15px; font-weight: 700; color: #f7fafc; letter-spacing: 0.3px; }
.cg-header-sub { font-size: 11px; color: #718096; margin-top: 1px; }
.cg-close-btn {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 7px; color: #718096; cursor: pointer; font-size: 13px;
  transition: all 0.15s ease;
}
.cg-close-btn:hover { background: rgba(255,255,255,0.1); color: #e2e8f0; }

/* Stats Bar */
.cg-stats-bar {
  display: flex; align-items: center; gap: 0;
  padding: 10px 22px;
  background: rgba(255,255,255,0.02);
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.cg-stat-item { display: flex; align-items: baseline; gap: 5px; padding: 0 14px; }
.cg-stat-item:first-child { padding-left: 0; }
.cg-stat-num { font-size: 18px; font-weight: 700; color: #667eea; }
.cg-stat-label { font-size: 11px; color: #718096; }
.cg-stat-divider { width: 1px; height: 24px; background: rgba(255,255,255,0.07); }
.cg-clear-all-btn {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  background: rgba(252,129,129,0.08); border: 1px solid rgba(252,129,129,0.25);
  border-radius: 6px; color: #fc8181; font-size: 11px; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
}
.cg-clear-all-btn:hover { background: rgba(252,129,129,0.15); border-color: #fc8181; }

/* Filters */
.cg-modal-filters {
  display: flex; align-items: flex-start; flex-direction: column; gap: 8px;
  padding: 12px 22px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.cg-filter-tabs {
  display: flex; gap: 4px;
  flex-wrap: nowrap;    /* Ko wrap — modal tự rộng ra theo số tab */
  overflow-x: auto;     /* Fallback scroll nếu vẫn không đủ chỗ */
}
.cg-filter-tabs::-webkit-scrollbar { height: 2px; }
.cg-filter-tabs::-webkit-scrollbar-thumb { background: #2d3748; border-radius: 2px; }
.cg-filter-tabs-wrap { flex-wrap: nowrap; }
.cg-filter-tab {
  padding: 5px 12px; border-radius: 6px; border: 1px solid transparent;
  background: transparent; color: #718096; font-size: 12px; font-weight: 500; cursor: pointer;
  transition: all 0.15s; white-space: nowrap;
}
.cg-filter-tab:hover { background: rgba(255,255,255,0.05); color: #a0aec0; }
.cg-filter-tab.active {
  background: rgba(102,126,234,0.15); border-color: rgba(102,126,234,0.4);
  color: #667eea; font-weight: 600;
}
.cg-filter-tab--custom { color: #a78bfa !important; border-color: rgba(102,126,234,0.1) !important; }
.cg-filter-tab--custom:hover { background: rgba(102,126,234,0.08) !important; color: #c4b5fd !important; }
.cg-filter-tab--custom.active { background: rgba(102,126,234,0.2) !important; border-color: rgba(102,126,234,0.5) !important; color: #c4b5fd !important; }
.cg-search-wrap {
  width: 100%; display: flex; align-items: center;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px; padding: 0 10px; gap: 8px;
  transition: border-color 0.15s;
}
.cg-search-wrap:focus-within { border-color: rgba(102,126,234,0.5); }
.cg-search-icon { color: #4a5568; font-size: 12px; }
.cg-search {
  flex: 1; background: transparent; border: none; outline: none;
  color: #e2e8f0; font-size: 13px; padding: 7px 0;
}
.cg-search-clear {
  background: none; border: none; color: #4a5568; cursor: pointer; font-size: 12px; padding: 2px;
}
.cg-search-clear:hover { color: #a0aec0; }

/* List */
.cg-list-header {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 22px 6px 18px;
  font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.8px; color: #4a5568;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.cg-lh-check { width: 16px; }
.cg-lh-name { flex: 1; }
.cg-lh-type { min-width: 90px; }
.cg-lh-override { min-width: 130px; text-align: right; margin-left: auto; }

.cg-modal-list { flex: 1; overflow-y: auto; }
.cg-modal-list::-webkit-scrollbar { width: 4px; }
.cg-modal-list::-webkit-scrollbar-track { background: transparent; }
.cg-modal-list::-webkit-scrollbar-thumb { background: #2d3748; border-radius: 4px; }

.cg-modal-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 22px 8px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.03);
  transition: background 0.12s;
}
.cg-modal-row:hover { background: rgba(255,255,255,0.03); }
.cg-row-custom { background: rgba(102,126,234,0.04); }
.cg-row-custom:hover { background: rgba(102,126,234,0.08); }

.cg-checkbox {
  width: 15px; height: 15px; cursor: pointer;
  accent-color: #667eea;
}
.cg-coin-name {
  flex: 1; font-weight: 600; font-size: 13px; color: #e2e8f0;
  display: flex; align-items: center; gap: 6px;
}
.cg-custom-label {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10px; color: #667eea; font-weight: 500;
}
.cg-tab-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px; font-weight: 600; padding: 3px 9px; border-radius: 5px;
  min-width: 80px; text-transform: uppercase; letter-spacing: 0.5px;
}
.cg-badge-spot    { background: rgba(43,108,176,0.25); color: #63b3ed; border: 1px solid rgba(43,108,176,0.35); }
.cg-badge-futures { background: rgba(192,86,33,0.25); color: #f6ad55; border: 1px solid rgba(192,86,33,0.35); }
.cg-badge-alpha   { background: rgba(107,70,193,0.25); color: #b794f4; border: 1px solid rgba(107,70,193,0.35); }
.cg-badge-null    { background: rgba(74,85,104,0.2);  color: #718096; border: 1px solid rgba(74,85,104,0.3); }

.cg-group-select {
  margin-left: auto;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.09);
  border-radius: 6px; color: #cbd5e0; padding: 4px 8px; font-size: 12px;
  cursor: pointer; transition: border-color 0.15s; min-width: 128px;
}
.cg-group-select:hover, .cg-group-select:focus { border-color: rgba(102,126,234,0.5); outline: none; }

.cg-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; padding: 40px; color: #4a5568; font-size: 13px;
}
.cg-empty i { font-size: 24px; }

/* Bulk action */
.cg-modal-bulk {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 12px 22px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: rgba(102,126,234,0.04);
}
.cg-bulk-info { display: flex; align-items: baseline; gap: 4px; }
.cg-bulk-count { font-size: 16px; font-weight: 700; color: #667eea; }
.cg-bulk-label { font-size: 12px; color: #718096; }
.cg-bulk-divider { width: 1px; height: 20px; background: rgba(255,255,255,0.07); margin: 0 4px; }
.cg-bulk-move { font-size: 12px; color: #a0aec0; font-weight: 500; }

.cg-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 13px; border-radius: 6px; border: none;
  font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.cg-btn-spot    { background: rgba(43,108,176,0.8); color: #fff; }
.cg-btn-spot:hover    { background: #2b6cb0; }
.cg-btn-futures { background: rgba(192,86,33,0.8); color: #fff; }
.cg-btn-futures:hover { background: #c05621; }
.cg-btn-alpha   { background: rgba(107,70,193,0.8); color: #fff; }
.cg-btn-alpha:hover   { background: #6b46c1; }
.cg-btn-custom  { background: rgba(102,126,234,0.2); color: #a78bfa; border: 1px solid rgba(102,126,234,0.3); }
.cg-btn-custom:hover  { background: rgba(102,126,234,0.35); color: #c4b5fd; }
/* Reset: danger color, tách biệt rõ — không nhầm với nút chuyển */
.cg-btn-reset   {
  background: rgba(197,48,48,0.15);
  color: #fc8181;
  border: 1.5px solid rgba(197,48,48,0.45);
  margin-left: 4px;
}
.cg-btn-reset:hover   { background: rgba(197,48,48,0.35); border-color: #fc8181; color: #fff; }
/* Push separator — đẩy Reset sang phải */
.cg-bulk-divider--push { flex: 1; background: transparent; }

/* Summary */
.cg-modal-summary {
  padding: 12px 22px;
  border-top: 1px solid rgba(255,255,255,0.05);
  max-height: 110px; overflow-y: auto;
  background: rgba(255,255,255,0.01);
}
.cg-summary-title { font-size: 11px; font-weight: 600; color: #4a5568; text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 8px; }
.cg-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.cg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  border-radius: 20px; padding: 3px 10px 3px 8px; font-size: 11px; font-weight: 500;
  cursor: default; transition: filter 0.15s;
}
.cg-chip:hover { filter: brightness(1.15); }
.cg-chip-spot    { background: rgba(43,108,176,0.2); color: #63b3ed; border: 1px solid rgba(43,108,176,0.3); }
.cg-chip-futures { background: rgba(192,86,33,0.2); color: #f6ad55; border: 1px solid rgba(192,86,33,0.3); }
.cg-chip-alpha   { background: rgba(107,70,193,0.2); color: #b794f4; border: 1px solid rgba(107,70,193,0.3); }
.cg-chip-x {
  cursor: pointer; color: inherit; opacity: 0.6;
  display: flex; align-items: center;
  transition: opacity 0.15s;
}
.cg-chip-x:hover { opacity: 1; }
/* ─── End Custom Groups ──────────────────────────────────── */
/* ─── Reset Confirm Modal ──────────────────────────────── */
.cg-confirm-modal {
  background: #13161d;
  border: 1px solid rgba(252,129,129,0.2);
  border-radius: 14px;
  width: 440px; max-width: 96vw;
  display: flex; flex-direction: column; overflow: hidden;
  color: #e2e8f0;
  box-shadow: 0 25px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(252,129,129,0.1);
}
.cg-confirm-header {
  display: flex; align-items: center; gap: 14px;
  padding: 20px 22px 16px;
  background: linear-gradient(180deg, #1f1a1a 0%, #13161d 100%);
  border-bottom: 1px solid rgba(252,129,129,0.1);
}
.cg-confirm-icon {
  width: 40px; height: 40px; flex-shrink: 0;
  background: rgba(252,129,129,0.12); border: 1px solid rgba(252,129,129,0.25);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: #fc8181;
}
.cg-confirm-title { font-size: 15px; font-weight: 700; color: #f7fafc; }
.cg-confirm-sub { font-size: 11px; color: #718096; margin-top: 2px; }
.cg-confirm-body { padding: 20px 22px; }
.cg-confirm-desc {
  font-size: 13px; color: #a0aec0; line-height: 1.6; margin-bottom: 16px;
}
.cg-confirm-desc strong { color: #e2e8f0; }
.cg-confirm-hint {
  font-size: 12px; color: #718096; margin-bottom: 10px;
}
.cg-confirm-code {
  background: rgba(102,126,234,0.15); border: 1px solid rgba(102,126,234,0.3);
  border-radius: 4px; padding: 1px 7px;
  font-family: monospace; color: #a78bfa; font-size: 13px;
}
.cg-confirm-input {
  width: 100%; box-sizing: border-box;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px; color: #e2e8f0; font-size: 14px;
  padding: 10px 14px; outline: none;
  transition: border-color 0.15s;
}
.cg-confirm-input:focus { border-color: rgba(252,129,129,0.5); }
.cg-confirm-input::placeholder { color: #4a5568; }
.cg-confirm-footer {
  display: flex; gap: 10px; justify-content: flex-end;
  padding: 14px 22px;
  border-top: 1px solid rgba(255,255,255,0.05);
  background: rgba(255,255,255,0.01);
}
.cg-confirm-btn-cancel {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 7px;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
  color: #a0aec0; font-size: 13px; font-weight: 500; cursor: pointer;
  transition: all 0.15s;
}
.cg-confirm-btn-cancel:hover { background: rgba(255,255,255,0.09); color: #e2e8f0; }
.cg-confirm-btn-ok {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border-radius: 7px;
  background: rgba(252,129,129,0.8); border: none;
  color: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
}
.cg-confirm-btn-ok:hover:not(:disabled) { background: #fc8181; }
.cg-confirm-btn-ok:disabled {
  opacity: 0.35; cursor: not-allowed;
}
/* ─── End Reset Confirm Modal ──────────────────────────── */

/* ─── Custom Tabs ───────────────────────────────────── */
.tab-custom {
  background: rgba(102,126,234,0.08) !important;
  border-color: rgba(102,126,234,0.2) !important;
  color: #a78bfa !important;
}
.tab-custom.active,
.tab-custom:hover {
  background: rgba(102,126,234,0.2) !important;
  border-color: rgba(102,126,234,0.5) !important;
  color: #c4b5fd !important;
}
.tab-add-new {
  background: rgba(255,255,255,0.04) !important;
  border: 1.5px dashed rgba(255,255,255,0.15) !important;
  color: #718096 !important;
  width: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.tab-add-new:hover {
  border-color: rgba(102,126,234,0.4) !important;
  color: #a78bfa !important;
  background: rgba(102,126,234,0.08) !important;
}
.tab-count-badge--custom {
  background: rgba(102,126,234,0.3) !important;
  color: #c4b5fd !important;
}
/* ─── Tab Manager Modal ──────────────────────────────── */
.tm-modal { width: 420px; max-width: 94vw; }
.tm-create-row {
  display: flex; gap: 10px; align-items: center;
  padding: 16px 22px 0;
}
.tm-tab-list {
  padding: 12px 22px;
  max-height: 260px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 6px;
}
.tm-tab-row {
  display: flex; align-items: center; justify-content: space-between;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  padding: 10px 14px;
  transition: background 0.15s;
}
.tm-tab-row:hover { background: rgba(255,255,255,0.05); }
.tm-tab-info {
  display: flex; align-items: center; gap: 8px;
  flex: 1; min-width: 0;
}
.tm-tab-name { font-size: 13px; font-weight: 600; color: #e2e8f0; }
.tm-tab-coin-count {
  font-size: 11px; font-weight: 600;
  background: rgba(102,126,234,0.2); color: #a78bfa;
  border-radius: 10px; padding: 1px 8px;
}
.tm-tab-coin-count.tm-empty {
  background: rgba(255,255,255,0.05); color: #4a5568;
}
.tm-delete-btn {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px;
  background: rgba(252,129,129,0.1); border: 1px solid rgba(252,129,129,0.2);
  color: #fc8181; cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.tm-delete-btn:hover:not(:disabled) {
  background: rgba(252,129,129,0.25); border-color: rgba(252,129,129,0.4);
}
.tm-delete-btn:disabled { opacity: 0.2; cursor: not-allowed; }
/* ─── End Custom Tabs ────────────────────────────────── */
</style>

